import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Header from './Header';
import {
  Users,
  Eye,
  Download,
  Search,
  Filter,
  DollarSign,
  MapPin,
  Mail,
  Phone,
  Calendar,
  FileText,
  BarChart3,
  Award,
  Target,
  CheckCircle,
  AlertCircle,
  Send,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';

const ModalViewPlotsCustomers = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;

  let business_locationIDMain = localStorage.getItem('business_locationIDMain');

  let userCompanyID = localStorage.getItem('companyID')
  if(userCompanyID === 'Admin'){
   userCompanyID = 1;
  }
  userCompanyID = JSON.parse(userCompanyID)

  let userID = localStorage.getItem('userID')
  if(userID === 'Admin'){
    userID = 1;
  }
  userID = JSON.parse(userID)

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZM', { style: 'currency', currency: 'ZMW' }).format(amount);
  };

  const formatCurrencyTable = (amount) => {
    const number = typeof amount === 'number' ? amount : parseFloat(amount);
    return new Intl.NumberFormat('en-ZM', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2}).format(number);
  };

  const [DBtablePlotsCustomers, setDBtablePlotsCustomers] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Filter data based on search input
  useEffect(() => {
    if (searchValue.trim() === '') {
      setFilteredData(DBtablePlotsCustomers);
    } else {
      const searchLower = searchValue.toLowerCase();
      const filtered = DBtablePlotsCustomers.filter(customer => 
        Object.values(customer).some(value => 
          value && value.toString().toLowerCase().includes(searchLower)
        )
      );
      setFilteredData(filtered);
    }
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchValue, DBtablePlotsCustomers]);

  // Calculate pagination data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  useEffect(() => {
    fetchDataForTablePlotsCustomers();
  }, []);

  const fetchDataForTablePlotsCustomers = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('apiType', 'fetchViewPlotsCustomers');
    formData.append('companyID', userCompanyID);
    formData.append('business_locationIDMain', business_locationIDMain);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setDBtablePlotsCustomers(response.data);
      setFilteredData(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching data:', error);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page when rows per page changes
  };

  const exportPDFPlotsCustomers = () => {
    const doc = new jsPDF('landscape');
    const tableRows = [];

    filteredData.forEach((row, index) => {
      tableRows.push([
        index + 1,
        row.businessLocationName,
        row.plotNumber,
        row.plotNumberNew,
        row.name,
        row.dateContractSigned,
        row.lastDatePaid,
        formatCurrencyTable(row.plotPrice),
        formatCurrencyTable(row.plotPricesNew),
        formatCurrencyTable(row.monthlyInstallments),
        formatCurrencyTable(row.paidToNHEF),
        formatCurrencyTable(row.totalAmountPaid),
        formatCurrencyTable(row.balance)
      ]);
    });

    doc.autoTable({
      head: [['SN', 'Location', 'Old Plot#', 'New Plot#', 'Name', 'Contract Date', 'Last Paid', 'Old Price', 'New Price', 'Monthly', 'NHEF', 'Total Paid', 'Balance']],
      body: tableRows,
      margin: { top: 20 },
      styles: {
        fontSize: 7,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold'
      },
      didDrawPage: (data) => {
        doc.setFontSize(10);
        doc.text(`Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
      }
    });

    doc.save('plots-customers-report.pdf');
  };

  const calculateTotals = () => {
    const totals = DBtablePlotsCustomers.reduce((acc, customer) => {
      acc.totalAmountPaid += parseFloat(customer.totalAmountPaid) || 0;
      acc.totalBalance += parseFloat(customer.balance) || 0;
      acc.totalPlotValue += parseFloat(customer.plotPricesNew) || 0;
      acc.totalCustomers += 1;
      return acc;
    }, { 
      totalAmountPaid: 0, 
      totalBalance: 0, 
      totalPlotValue: 0,
      totalCustomers: 0 
    });
    
    return totals;
  };

  const totals = calculateTotals();

  const handleViewStatement = (customer) => {
    setSelectedCustomer(customer);
    setShowStatementModal(true);
  };

  const handleSendEmail = (customer) => {
    setSelectedCustomer(customer);
    setEmailSubject(`Statement for ${customer.name} - Plot ${customer.plotNumber}`);
    setEmailMessage(`Dear ${customer.name},\n\nPlease find attached your plot payment statement.\n\nRegards,\nManagement Team`);
    setShowEmailModal(true);
  };

  const sendEmailCustomerStatementPDF = async () => {
    setSendingEmail(true);
    setEmailError('');
    setEmailSuccess(false);

    try {
      const formData = new FormData();
      formData.append('apiType', 'sendEmailCustomerStatementPDF');
      formData.append('email', selectedCustomer.customersEmail);
      formData.append('subject', emailSubject);
      formData.append('message', emailMessage);
      formData.append('customerName', selectedCustomer.name);
      formData.append('plotNumber', selectedCustomer.plotNumber);

      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSendingEmail(false);
      if (response.data.includes('success')) {
        setEmailSuccess(true);
        setTimeout(() => {
          setShowEmailModal(false);
          setEmailSuccess(false);
        }, 2000);
      } else {
        setEmailError(response.data);
      }
    } catch (error) {
      setSendingEmail(false);
      setEmailError('Failed to send email. Please try again.');
    }
  };

  const sendEmailPlotsLoginCredentials = async (customerId, customerEmail) => {
    try {
      const formData = new FormData();
      formData.append('apiType', 'sendEmailPlotsLoginCredentials');
      formData.append('id', customerId);
      formData.append('email', customerEmail);

      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      alert(response.data);
    } catch (error) {
      alert('Failed to send login credentials. Please try again.');
    }
  };

  const comfirmCustomersAvailability = async (customerId) => {
    try {
      const formData = new FormData();
      formData.append('apiType', 'comfirmClientAvailability');
      formData.append('customerID', customerId);
      formData.append('customerType', 'Plots');

      const response = await axios.post(`${server_app_api_php}/server/payment_remindersEmailList.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      alert(response.data);
    } catch (error) {
      alert('Failed to confirm customer availability. Please try again.');
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);
      
      pageNumbers.push(1);
      
      if (startPage > 2) {
        pageNumbers.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }
      
      if (totalPages > 1) {
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  return (
    <>
      <Header />
      <main className="lg:ml-64 pt-16 min-h-screen bg-gray-50">
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Plot Customers</h1>
                    <p className="text-gray-600">Manage and view all plot customers</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={exportPDFPlotsCustomers}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl hover:opacity-90 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export PDF</span>
                  </button>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Customers</p>
                      <p className="text-2xl font-bold text-gray-900">{totals.totalCustomers}</p>
                    </div>
                    <Users className="w-8 h-8 text-purple-500" />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Plot Value</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.totalPlotValue)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-blue-500" />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Amount Paid</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.totalAmountPaid)}</p>
                    </div>
                    <BarChart3 className="w-8 h-8 text-green-500" />
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Balance</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.totalBalance)}</p>
                    </div>
                    <Target className="w-8 h-8 text-orange-500" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Search customers..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="pl-10 pr-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white w-64"
                      />
                    </div>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => handleRowsPerPageChange(e.target.value)}
                      className="px-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="5">5 entries</option>
                      <option value="10">10 entries</option>
                      <option value="25">25 entries</option>
                      <option value="50">50 entries</option>
                      <option value="100">100 entries</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Filter className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      Showing {paginatedData.length} of {filteredData.length} customers
                      {searchValue && ` (${DBtablePlotsCustomers.length} total)`}
                    </span>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                {loading ? (
                  <div className="p-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="mt-4 text-gray-500">Loading customers data...</p>
                  </div>
                ) : filteredData.length === 0 ? (
                  <div className="p-12 text-center">
                    <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No customers found matching your search</p>
                  </div>
                ) : (
                  <>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-purple-500 to-pink-600">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">SN</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            <MapPin className="w-4 h-4 inline mr-1" />
                            Location
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Plot #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            <Phone className="w-4 h-4 inline mr-1" />
                            Phone
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            Contract Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            
                            Plot Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Monthly</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Total Paid</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Balance</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedData.map((val, index) => (
                          <tr key={index} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(currentPage - 1) * rowsPerPage + index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center">
                                <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                                {val.businessLocationName}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-blue-600">{val.plotNumber}</div>
                                <div className="text-xs text-gray-500">New: {val.plotNumberNew}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{val.name}</div>
                                <div className="text-xs text-gray-500">{val.customersEmail}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {val.customersPhone}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {val.dateContractSigned}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatCurrency(val.plotPricesNew)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(val.monthlyInstallments)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                              {formatCurrency(val.totalAmountPaid)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                parseFloat(val.balance) > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                              }`}>
                                {formatCurrency(val.balance)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleViewStatement(val)}
                                  className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                >
                                  <Eye className="w-4 h-4" />
                                  <span>View</span>
                                </button>
                                <button
                                  onClick={() => handleSendEmail(val)}
                                  className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                                >
                                  <Send className="w-4 h-4" />
                                  <span>Email</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan="8" className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                            Totals:
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(totals.totalAmountPaid)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                            {formatCurrency(totals.totalBalance)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>

                    {/* Custom Pagination */}
                    {totalPages > 1 && (
                      <div className="px-6 py-4 border-t border-gray-200 bg-white">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="text-sm text-gray-500">
                            Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredData.length)} of {filteredData.length} entries
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handlePageChange(1)}
                              disabled={currentPage === 1}
                              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              <ChevronsLeft className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            
                            {getPageNumbers().map((pageNum, index) => (
                              <button
                                key={index}
                                onClick={() => typeof pageNum === 'number' && handlePageChange(pageNum)}
                                disabled={pageNum === '...'}
                                className={`min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium ${
                                  currentPage === pageNum
                                    ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                                    : 'border border-gray-300 hover:bg-gray-50'
                                } ${pageNum === '...' ? 'cursor-default' : ''}`}
                              >
                                {pageNum}
                              </button>
                            ))}
                            
                            <button
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handlePageChange(totalPages)}
                              disabled={currentPage === totalPages}
                              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              <ChevronsRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div>
                    Showing {filteredData.length} of {DBtablePlotsCustomers.length} customers
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={exportPDFPlotsCustomers}
                      className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg hover:opacity-90 transition-all"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download PDF</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statement Modal */}
        {showStatementModal && selectedCustomer && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              
              <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                <div className="bg-gradient-to-r from-purple-500 to-pink-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-white">Customer Statement</h3>
                    <button
                      onClick={() => setShowStatementModal(false)}
                      className="text-white hover:text-gray-200"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                
                <div className="px-6 py-6">
                  {/* Customer Details */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500">Customer Name</p>
                        <p className="font-medium text-gray-900">{selectedCustomer.name}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500">Email Address</p>
                        <p className="font-medium text-gray-900">{selectedCustomer.customersEmail}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500">Phone Number</p>
                        <p className="font-medium text-gray-900">{selectedCustomer.customersPhone}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500">Plot Number</p>
                        <p className="font-medium text-gray-900">{selectedCustomer.plotNumber}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Financial Summary */}
                  <div className="mb-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Financial Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-green-50 rounded-xl">
                        <p className="text-sm text-green-600">Plot Price</p>
                        <p className="text-2xl font-bold text-green-700">{formatCurrency(selectedCustomer.plotPricesNew)}</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-xl">
                        <p className="text-sm text-blue-600">Total Paid</p>
                        <p className="text-2xl font-bold text-blue-700">{formatCurrency(selectedCustomer.totalAmountPaid)}</p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-xl">
                        <p className="text-sm text-red-600">Balance</p>
                        <p className="text-2xl font-bold text-red-700">{formatCurrency(selectedCustomer.balance)}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Payment Details */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Payment Details</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {/* Sample payment data - you should replace with actual data */}
                          <tr>
                            <td className="px-4 py-3 text-sm text-gray-900">{selectedCustomer.dateContractSigned}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {formatCurrency(selectedCustomer.monthlyInstallments)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">Monthly Installment</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                Paid
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => handleSendEmail(selectedCustomer)}
                      className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:opacity-90 transition-all"
                    >
                      Send Statement
                    </button>
                    <button
                      onClick={() => setShowStatementModal(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Modal */}
        {showEmailModal && selectedCustomer && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              
              <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-white">Send Email Statement</h3>
                    <button
                      onClick={() => setShowEmailModal(false)}
                      className="text-white hover:text-gray-200"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                
                <div className="px-6 py-6">
                  {emailSuccess && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-green-800">Email sent successfully!</span>
                      </div>
                    </div>
                  )}
                  
                  {emailError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <span className="text-red-800">{emailError}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        To
                      </label>
                      <input
                        type="email"
                        value={selectedCustomer.customersEmail}
                        readOnly
                        className="w-full px-4 py-2 bg-gray-100 rounded-lg border border-gray-300"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message
                      </label>
                      <textarea
                        value={emailMessage}
                        onChange={(e) => setEmailMessage(e.target.value)}
                        rows="6"
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowEmailModal(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={sendEmailCustomerStatementPDF}
                      disabled={sendingEmail}
                      className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {sendingEmail ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default ModalViewPlotsCustomers;